import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logError, ErrorCodes } from '@/lib/errorLogging';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

/**
 * Global Error Boundary Component
 * 
 * Catches React rendering errors and logs them to the database.
 * 
 * Usage:
 *   <ErrorBoundary componentName="AgentDetail">
 *     <AgentDetail />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  async componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, componentName } = this.props;

    // Log to database
    const errorId = await logError({
      type: 'frontend',
      source: componentName || 'ErrorBoundary',
      message: error.message,
      error,
      errorCode: ErrorCodes.FE_RENDER,
      severity: 'critical',
      metadata: {
        componentStack: errorInfo.componentStack,
        componentName: componentName
      }
    });

    this.setState({ errorId });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Also log to console for development
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorId: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
          <div className="bg-destructive/10 rounded-full p-4 mb-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">Qualcosa è andato storto</h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            Si è verificato un errore imprevisto. Il nostro team è stato notificato.
          </p>
          {this.state.errorId && (
            <p className="text-xs text-muted-foreground mb-4 font-mono">
              Error ID: {this.state.errorId}
            </p>
          )}
          <div className="flex gap-3">
            <Button variant="secondary" onClick={this.handleRetry}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Riprova
            </Button>
            <Button onClick={this.handleReload}>
              Ricarica pagina
            </Button>
          </div>
          {import.meta.env.DEV && this.state.error && (
            <details className="mt-6 text-left w-full max-w-2xl">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                Dettagli errore (dev only)
              </summary>
              <pre className="mt-2 p-4 bg-muted rounded-lg text-xs overflow-auto">
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC to wrap a component with error boundary
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName?: string
): React.FC<P> {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary componentName={componentName || WrappedComponent.displayName || WrappedComponent.name}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
